/**
 * Audience-specific copy + structure for the family-office / small-fund
 * landing pages. Both pages render through `AudienceLandingPage` using
 * one of these configs.
 */

export interface ProofPoint {
  metric: string;
  label: string;
}

export interface FeatureBlock {
  eyebrow: string;
  headline: string;
  body: string;
}

export interface AudienceLandingConfig {
  /** URL slug, e.g. "family-offices" */
  slug: string;
  /** Short audience descriptor for body copy ("family offices", "small VC funds") */
  audienceLabel: string;
  /** Top-bar / OG-title audience name */
  audienceTitle: string;
  /** Hero H1 */
  heroHeadline: string;
  /** Hero sub */
  heroSub: string;
  /** Eyebrow line above hero */
  heroEyebrow: string;
  /** Three short proof metrics */
  proofs: ProofPoint[];
  /** Feature sections (alternating dark/light bands) */
  features: FeatureBlock[];
  /** Lead-source tag persisted with intake submissions */
  leadSource: string;
  /** Optional three-card "ways to engage" layup. Only the dedicated
   *  InvestorLandingPage reads this; the shared AudienceLandingPage
   *  ignores it, so family-office / fund pages are unaffected. */
  engage?: EngageLayup;
}

/**
 * Optional "three ways to engage" layup, rendered by the dedicated
 * InvestorLandingPage (not the shared AudienceLandingPage). Modeled on
 * the mens-health PILLARS three-card layout: a voice-AI call-in card, a
 * chat card pointing back at the hero chatbox, and a book-a-walkthrough
 * card. Kept here so the copy lives with the rest of the audience config.
 */
export interface EngageLayup {
  /** Section eyebrow + headline */
  eyebrow: string;
  headline: string;
  sub: string;
  /** Card 1 — voice-AI call-in (Sloan, the Alfred Voice tenant persona) */
  voiceTitle: string;
  voiceBody: string;
  /** Card 2 — chat-now, scrolls back to the hero chatbox */
  chatTitle: string;
  chatBody: string;
  /** Card 3 — book a 15-minute walkthrough */
  bookTitle: string;
  bookBody: string;
  /** External scheduling URL for the walkthrough card */
  bookHref: string;
}

const SHARED_HERO_HEADLINE = 'Better picks. Faster decisions. Every candidate.';

export const FAMILY_OFFICE_CONFIG: AudienceLandingConfig = {
  slug: 'family-offices',
  audienceLabel: 'family offices',
  audienceTitle: 'Family Offices',
  heroEyebrow: 'For family offices that run lean',
  heroHeadline: SHARED_HERO_HEADLINE,
  heroSub:
    'VentureOS turns your own deal history into a calibrated picking engine, then operates it across every candidate you see — diligence rigor and decision discipline without hiring a full analyst team.',
  proofs: [
    { metric: '40–60%', label: 'shorter time to decision, measured against your Wk-1 baseline' },
    { metric: '100%', label: 'diligence coverage — every candidate gets the same rigor' },
    { metric: '6', label: 'AI expert agents (Market · Financial · Technical · GTM · Team · ESG)' },
  ],
  features: [
    {
      eyebrow: 'Calibrated to you',
      headline: 'Your rubric, sourced from your own track record.',
      body:
        "We ingest your past deals — winners and losses — and build a rubric calibrated to your actual revealed thesis, not a generic one. Back-tested against a held-out set of your own history so you can see it ranking your thrivers above your strugglers before you act on a new deal.",
    },
    {
      eyebrow: 'Multi-agent diligence',
      headline: 'Six AI agents run on every candidate, automatically.',
      body:
        'Every new candidate gets a Deal/No-Deal Detailed Report — Market, Financial, Technical, GTM, Team, and ESG agents working in parallel, plus an 8-criterion rubric, Capital Readiness Score across 7 weighted dimensions, and Claude Vision deck analysis. No skipped diligence, no inconsistent coverage.',
    },
    {
      eyebrow: 'Values you can score',
      headline: 'Mission alignment, not just math.',
      body:
        'Define the 5–10 "careabouts" that make a company belong with you — patient-first outcomes, evidence-based practice, equity in access, whatever grounds your portfolio — and every candidate gets scored on values fit next to financial fit. Catch the strong-on-paper deals that miss on mission before the partner conversation.',
    },
    {
      eyebrow: 'Built to compound',
      headline: 'Every decision sharpens the next one.',
      body:
        'The Decision Journal captures every IC call, conviction, and reasoning. As outcomes develop, the system reports which of your signals actually predicted winners — and where you were over- or under-confident. Your picking improves with evidence, not vibes.',
    },
  ],
  leadSource: 'landing-family-offices',
};

export const FUND_CONFIG: AudienceLandingConfig = {
  slug: 'for-funds',
  audienceLabel: 'small VC funds',
  audienceTitle: 'Funds',
  heroEyebrow: 'For small funds that don\'t want to hire a bench',
  heroHeadline: SHARED_HERO_HEADLINE,
  heroSub:
    "VentureOS turns your fund's track record into a calibrated picking engine, then operates it across every candidate and every analyst on the bench — rigor and consistency without scaling headcount.",
  proofs: [
    { metric: '40–60%', label: 'shorter time to decision per candidate' },
    { metric: '100%', label: 'diligence coverage — every deal screened the same way' },
    { metric: '3', label: 'scores per analyst per deal: fit, compliance, judgment deviation' },
  ],
  features: [
    {
      eyebrow: 'Calibrated to you',
      headline: 'Your rubric, sourced from your fund\'s track record.',
      body:
        'We ingest your past deals — invested and passed, winners and losses — and build a rubric calibrated to your fund\'s actual revealed thesis. Back-tested on a held-out set of your own decisions, so you can see the engine ranking your thrivers above your strugglers before you trust it on a live deal.',
    },
    {
      eyebrow: 'Diligence engine',
      headline: 'Six AI agents + rubric + CRS on every candidate.',
      body:
        'Every new candidate gets a Deal/No-Deal Detailed Report from six AI agents (Market, Financial, Technical, GTM, Team, ESG), an 8-criterion rubric score, a 7-dimension Capital Readiness Score, and Claude Vision deck analysis. Consistent rigor, automatically, on every deal that enters your funnel.',
    },
    {
      eyebrow: 'Bench performance',
      headline: 'Three measured scores on every analyst, every deal.',
      body:
        "Founder→analyst routing surfaces best, 2nd-best, 3rd-best fit. Compliance scoring tracks whether each analyst followed your playbook. Benchmark-deviation scoring tracks whether their judgment matched your calibrated rubric. Two coaching levers — process and judgment — surfaced on the admin dashboard.",
    },
    {
      eyebrow: 'Built to compound',
      headline: 'Every decision sharpens the next one.',
      body:
        "The Decision Journal captures every IC call, conviction, and reasoning. As outcomes develop, the system reports which of your signals actually predicted winners — and where the fund was over- or under-confident. Your picking improves with evidence, not vibes — and that compounding edge is what makes the platform pay off past the pilot.",
    },
  ],
  leadSource: 'landing-for-funds',
};

export const INVESTORS_CONFIG: AudienceLandingConfig = {
  slug: 'for-investors',
  audienceLabel: 'investors',
  audienceTitle: 'Investors',
  heroEyebrow: 'For investment groups still running deal flow by hand',
  heroHeadline: 'A big team\'s rigor, without hiring the bench.',
  heroSub:
    'Accelerators, syndicates, corporate venture arms, grant-makers, angel groups, family offices — the work rhymes: hundreds of inbound opportunities, a lean team, and a pipeline scattered across spreadsheets and email. VentureOS auto-researches every applicant, scores and matches each one across 7 weighted dimensions, runs 6-agent diligence on every candidate, and hosts the whole pipeline in one place — so a small team operates like a big one.',
  proofs: [
    { metric: '100%', label: 'of inbound screened the same way — no more deciding which apps get a real look' },
    { metric: '6', label: 'AI expert agents run on every candidate (Market · Financial · Technical · GTM · Team · ESG)' },
    { metric: '7', label: 'weighted dimensions scored per deal — calibrated fit, not a gut read' },
  ],
  features: [
    {
      eyebrow: 'High-volume intake',
      headline: 'Stop hand-screening hundreds of applications per cohort.',
      body:
        'Open-application accelerators and micro-VCs drown in inbound — a 3–4% accept rate means hundreds of decks read by hand every cohort, and the good ones get the same five minutes as the noise. VentureOS auto-researches every applicant, runs identical diligence on all of them, and ranks the pipeline — so your partners spend their attention where it pays off instead of grinding through a queue.',
    },
    {
      eyebrow: 'Triage at scale',
      headline: 'Open-RFP triage at 1–2% acceptance, without the manual grind.',
      body:
        'Grant-makers running open EOI and RFP channels face the harshest funnel of all: a thousand-plus applications a round, a 1–2% accept rate, and grants-ops staff reading every one. VentureOS applies your rubric consistently across the entire field, surfaces the candidates that genuinely match your criteria, and records the reasoning behind each call — so review is defensible and fast, not a bottleneck.',
    },
    {
      eyebrow: 'Pipeline that lives somewhere',
      headline: 'Get your venture team off spreadsheets and email.',
      body:
        'New corporate venture arms and lean angel groups run real deal flow in shared inboxes, Airtable, and a CRM nobody updates. VentureOS hosts the whole pipeline — sourcing, research, scoring, diligence, decisions — in one place, with a Decision Journal that captures every IC call so the next decision is sharper than the last. One person can run it; a growing portfolio won\'t break it.',
    },
    {
      eyebrow: 'Coordination, handled',
      headline: 'SPV admin and member coordination, without the spreadsheet sprawl.',
      body:
        'Syndicates juggle per-deal SPV mechanics and member updates across thousands of members and rounds; family offices and micro-VCs track a growing book of holdings, follow-ons, and side letters on tabs that drift out of date. VentureOS keeps the pipeline, the portfolio, and the people in sync — so the operating load of doing more deals stops scaling linearly with headcount.',
    },
  ],
  leadSource: 'landing-for-investors',
  engage: {
    eyebrow: 'Three ways to start',
    headline: 'See it on your own deal flow — pick whichever is easiest.',
    sub:
      'Same intake either way: about a minute of questions, then we line up a 15-minute walkthrough on your real pipeline.',
    voiceTitle: 'Pick a person — they\'ll call you',
    voiceBody:
      'Choose who you\'d like to talk to. Your pick is a VentureOS voice AI — give us a name and number and they\'ll call you right back from (414) 310-0078. Ask how the platform would handle your intake, your rubric, your portfolio, in plain conversation with no form to fill out. They take your details and hand off to the team for the walkthrough.',
    chatTitle: 'Chat with us now',
    chatBody:
      'The chatbox at the top of this page is live. Tell it who you are and what your pipeline looks like — about a minute — and we\'ll follow up to schedule. No download, no login, no sales rep on the line.',
    bookTitle: 'Book a 15-min walkthrough',
    bookBody:
      'Already know you want to see it? Grab a time directly and we\'ll come ready — a live look at how VentureOS researches, scores, and diligences deals against your thesis, on your kind of pipeline.',
    bookHref: 'https://getventureos.ai/for-investors',
  },
};
